// Timezone-aware helpers for reminders. Reminders are anchored to the RESIDENT's
// timezone: the creator picks a wall-clock time meaning "the resident's local
// time," we convert it to an absolute instant (epoch ms) using that zone, and we
// format it back in that zone on every screen — so a family member in another
// timezone still means "2 PM at the facility." No external dependency; uses the
// built-in Intl APIs (DST-safe).

export const MINUTE_MS = 60 * 1000;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;
export const UNIT_MS = { minutes: MINUTE_MS, hours: HOUR_MS, days: DAY_MS };
export const DEFAULT_WARN_MS = 30 * MINUTE_MS;

// This device's IANA timezone, e.g. "America/New_York".
export function deviceTimeZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
  catch { return "UTC"; }
}

// Offset (ms) of `tz` from UTC at a given absolute instant (handles DST).
function tzOffsetMs(instantMs, tz) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const p = {};
  for (const part of dtf.formatToParts(new Date(instantMs))) p[part.type] = part.value;
  const hour = p.hour === "24" ? "00" : p.hour; // some engines emit 24 at midnight
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +hour, +p.minute, +p.second);
  return asUTC - instantMs;
}

// Interpret a wall-clock ("YYYY-MM-DDTHH:mm" from <input type=datetime-local>)
// as local time in `tz`, returning the absolute instant (epoch ms).
export function wallClockToInstant(local, tz) {
  if (!local) return null;
  const [d, t] = local.split("T");
  if (!d || !t) return null;
  const [y, mo, day] = d.split("-").map(Number);
  const [h, mi] = t.split(":").map(Number);
  const guess = Date.UTC(y, mo - 1, day, h, mi);
  let inst = guess - tzOffsetMs(guess, tz);   // first pass
  inst = guess - tzOffsetMs(inst, tz);        // settle DST boundaries
  return inst;
}

// Format an instant as a datetime-local input value ("YYYY-MM-DDTHH:mm") in `tz`.
export function instantToWallClock(ms, tz) {
  if (ms == null) return "";
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
  const p = {};
  for (const part of dtf.formatToParts(new Date(ms))) p[part.type] = part.value;
  const hour = p.hour === "24" ? "00" : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`;
}

// Human label for a reminder time, in the resident's zone, e.g. "Mon 3:00 PM".
export function formatReminder(ms, tz) {
  if (ms == null) return "";
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz, weekday: "short", hour: "numeric", minute: "2-digit",
  }).format(new Date(ms));
}

// Human label for an alarm's scheduled event time, shown on the tile. For a
// one-time alarm: "Mon 3:00 PM"; daily: "3:00 PM daily"; weekly: "3:00 PM ·
// Mon Wed". Empty when there's no alarm/event time.
export function formatAlarm(msg, tz) {
  if (!msg || !msg.alert || msg.actionAt == null) return "";
  const recur = msg.recur || "once";
  const t = new Intl.DateTimeFormat(undefined, {
    timeZone: tz, hour: "numeric", minute: "2-digit",
  }).format(new Date(msg.actionAt));
  if (recur === "once") return formatReminder(msg.actionAt, tz);
  if (recur === "daily") return `${t} daily`;
  if (recur === "weekly") {
    const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const days = (msg.recurDays || []).map((d) => WD[d]).join(" ");
    return days ? `${t} · ${days}` : t;
  }
  return t;
}

// Reminder lifecycle state: 'upcoming' | 'warning' | 'past' (null if not a reminder).
export function reminderState(actionAt, warnMs, now) {
  if (actionAt == null) return null;
  if (now >= actionAt) return "past";
  if (now >= actionAt - (warnMs || 0)) return "warning";
  return "upcoming";
}

// ---- Message scheduling (visibility windows + recurrence) ------------------
// A message may carry an optional schedule so it only appears within a window:
//   startAt   epoch ms — begins showing (absent => no schedule => always shown)
//   endAt     epoch ms — for 'once', stops showing (absent => open-ended / "Always")
//   recur     'once' | 'daily' | 'weekly'
//   recurDays number[] 0=Sun..6=Sat, for 'weekly'
// For 'daily'/'weekly', startAt/endAt supply the time-of-day WINDOW (and startAt
// its begin date); the window repeats each matching day. All evaluated in the
// resident's timezone so "9pm" means 9pm at the facility.

// Weekday 0=Sun..6=Sat for an instant in tz.
export function weekdayOf(ms, tz) {
  const wd = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short" }).format(new Date(ms));
  return { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[wd] ?? 0;
}

// Minutes since local midnight for an instant in tz (0..1439).
export function minutesOfDay(ms, tz) {
  const p = {};
  for (const part of new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hour12: false, hour: "2-digit", minute: "2-digit",
  }).formatToParts(new Date(ms))) p[part.type] = part.value;
  const h = p.hour === "24" ? 0 : +p.hour;
  return h * 60 + (+p.minute);
}

// Is a scheduled message visible at `now` (epoch ms) in `tz`? No schedule => true.
export function isMessageVisible(msg, now, tz) {
  const start = msg && msg.startAt;
  if (start == null) return true;              // unscheduled -> persistent
  if (now < start) return false;               // not begun yet
  const recur = msg.recur || "once";
  if (recur === "once") {
    return msg.endAt == null || now <= msg.endAt;
  }
  // daily / weekly: a time-of-day window that repeats
  const winStart = minutesOfDay(start, tz);
  const durMin = msg.endAt != null ? Math.max(0, Math.round((msg.endAt - start) / MINUTE_MS)) : 0;
  let inWindow;
  if (durMin >= 1440) {
    inWindow = true;                            // 24h+ window => all day
  } else {
    const winEnd = (winStart + durMin) % 1440;
    const nowTod = minutesOfDay(now, tz);
    inWindow = winStart <= winEnd
      ? (nowTod >= winStart && nowTod <= winEnd)
      : (nowTod >= winStart || nowTod <= winEnd); // wraps past midnight
  }
  if (!inWindow) return false;
  if (recur === "weekly") {
    const days = Array.isArray(msg.recurDays) ? msg.recurDays : [];
    if (!days.includes(weekdayOf(now, tz))) return false;
  }
  return true;
}

// Map the time-of-day of `refInstant` onto the day containing `now` (in tz), so a
// recurring alarm's event time applies to today's occurrence rather than the
// original date. Returns an absolute instant (epoch ms).
export function occurrenceInstant(refInstant, now, tz) {
  const day = instantToWallClock(now, tz).split("T")[0];       // YYYY-MM-DD today
  const time = instantToWallClock(refInstant, tz).split("T")[1]; // HH:mm of the event
  return wallClockToInstant(`${day}T${time}`, tz);
}

// Alarm lifecycle for a message with an event time (actionAt) + pre-event warn
// (warnMs): 'none' | 'upcoming' | 'warning' (glowing) | 'past' (grey). For
// daily/weekly recurrence the event time is applied to the CURRENT day so the
// glow → grey cycle repeats each occurrence. Only meaningful when msg.alert.
export function alarmState(msg, now, tz) {
  if (!msg || !msg.alert || msg.actionAt == null) return "none";
  const recur = msg.recur || "once";
  const eventAt = recur === "once" ? msg.actionAt : occurrenceInstant(msg.actionAt, now, tz);
  return reminderState(eventAt, msg.warnMs, now) || "none";
}

// Is the resident's screen within its daily quiet-hours (screen-off) window?
// offStart/offEnd are minutes-since-midnight in tz; equal or null => no quiet hours.
export function quietHoursActive(offStartMin, offEndMin, now, tz) {
  if (offStartMin == null || offEndMin == null || offStartMin === offEndMin) return false;
  const nowTod = minutesOfDay(now, tz);
  return offStartMin <= offEndMin
    ? (nowTod >= offStartMin && nowTod < offEndMin)
    : (nowTod >= offStartMin || nowTod < offEndMin); // overnight wrap
}
